/**
 * KVNR-Validator (Krankenversichertennummer) – Frontend-Version
 *
 * Validiert die Krankenversichertennummer nach § 290 SGB V.
 *
 * Format: 1 Großbuchstabe + 9 Ziffern = 10 Zeichen
 *   - Position 1:    Großbuchstabe (A–Z), kennzeichnet die ausstellende Kasse
 *   - Position 2–9:  8 Ziffern, individueller Teil
 *   - Position 10:   Prüfziffer (0–9)
 *
 * Prüfziffer-Algorithmus:
 *   1. Buchstabe → zweistellige Zahl (A=01, B=02, ..., Z=26)
 *   2. 10-stellige Ziffernfolge mit Gewichten 1,2,1,2,... multiplizieren
 *   3. Bei Produkt ≥ 10: Quersumme (z.B. 14 → 1+4=5)
 *   4. Summe mod 10 = Prüfziffer
 */

export interface KvnrValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Berechnet die Prüfziffer einer KVNR nach dem Verfahren gemäß § 290 SGB V.
 */
export function berechneKvnrPruefziffer(kvnrOhneP: string): number {
  const buchstabenWert = kvnrOhneP.charCodeAt(0) - 64; // A=1, B=2, ..., Z=26
  const zweistellig = buchstabenWert.toString().padStart(2, "0");
  const ziffernfolge = zweistellig + kvnrOhneP.substring(1, 9);

  let summe = 0;
  for (let i = 0; i < 10; i++) {
    const ziffer = parseInt(ziffernfolge[i], 10);
    const gewicht = i % 2 === 0 ? 1 : 2;
    let produkt = ziffer * gewicht;
    if (produkt >= 10) {
      produkt = Math.floor(produkt / 10) + (produkt % 10);
    }
    summe += produkt;
  }
  return summe % 10;
}

/**
 * Validiert eine KVNR. Gibt bei leerem Wert `valid: true` zurück
 * (Pflichtfeld-Prüfung ist Aufgabe des Backend-Validators, nicht des Formats).
 */
export function validateKVNR(value: string | null | undefined): KvnrValidationResult {
  if (!value || value.trim() === "") {
    return { valid: true }; // Leer = kein Formatfehler (Pflichtprüfung separat)
  }

  const trimmed = value.trim();

  // Länge prüfen
  if (trimmed.length !== 10) {
    return {
      valid: false,
      error: `Die Versichertennummer muss genau 10 Zeichen lang sein (aktuell: ${trimmed.length}).`,
    };
  }

  // Format prüfen: 1 Großbuchstabe + 9 Ziffern
  if (!/^[A-Z]\d{9}$/.test(trimmed)) {
    if (/^[a-z]\d{9}$/.test(trimmed)) {
      return {
        valid: false,
        error: "Die Versichertennummer muss mit einem Großbuchstaben beginnen.",
      };
    }
    if (!/^[A-Za-z]/.test(trimmed)) {
      return {
        valid: false,
        error: "Die Versichertennummer muss mit einem Buchstaben (A–Z) beginnen, gefolgt von 9 Ziffern.",
      };
    }
    return {
      valid: false,
      error: "Die Versichertennummer muss aus einem Großbuchstaben und 9 Ziffern bestehen (z.B. A123456780).",
    };
  }

  // Prüfziffer berechnen und vergleichen
  const erwartet = berechneKvnrPruefziffer(trimmed.substring(0, 9));
  const tatsaechlich = parseInt(trimmed[9], 10);

  if (erwartet !== tatsaechlich) {
    return {
      valid: false,
      error: `Prüfziffer ungültig (erwartet: ${erwartet}, erhalten: ${tatsaechlich}).`,
    };
  }

  return { valid: true };
}

/**
 * Convenience-Funktion: Gibt den Fehlertext zurück oder undefined.
 * Kann direkt als `error`-Prop in Controls.Input verwendet werden.
 */
export function getKvnrError(value: string | null | undefined): string | undefined {
  return validateKVNR(value).error;
}
